












































































































































































































































































































































































































/* eslint-disable no-underscore-dangle */
import { defineComponent, computed, watch, ref, onMounted, onUnmounted } from '@vue/composition-api';
import filesize from 'filesize';
import { Location } from 'vue-router';

import { prompt } from '@/components/Prompt';
import { snackbar } from '@/components/GlobalSnackbar';
import store from '@/store';
import type {
  Task,
  StatisticsOptions,
  VideoEvaluationResult,
} from '@/types';
import { useClient } from '@/use';
import { taskIsFinished } from '@/utils';

import {
  completedAnalysesOpen, tasks, latestTasks, latestTaskTotal, taskPollingActive,
} from '../state';

export default defineComponent({
  name: 'CompletedAnalysesMenu',
  setup() {
    const selectedTasks = ref<Task[]>([]);
    const fetchingTasks = ref(false);
    const deletingTasks = ref(false);
    let fetchQueued = false;
    let requestVersion = 0;
    const loading = computed(() => fetchingTasks.value || deletingTasks.value);
    const ongoingDisplayedTasks = computed(() => tasks.value.filter((task) => !taskIsFinished(task)));
    const loadingAnalysis = ref(false);
    const latestAddedTaskId = computed(() => store.state.application.latestAddedTaskId);
    const search = ref<string | null>(null);
    const searchTimeout = ref<number | null>(null);
    const taskTableInfo = ref({ total_number: 0 });
    const taskTableOptions = ref({
      page: 1,
      itemsPerPage: 15,
      sortBy: ['requested_at'],
      sortDesc: [true],
      groupBy: [],
      groupDesc: [],
      mustSort: false,
      multiSort: false,
    });
    const taskFetchTimeout = ref<number | null>(null);
    const postAnalysisDialog = ref<boolean>(false);
    // Conditionally add motion header
    const isSupervisor = computed(() => store.getters.isSupervisor);
    const headers = computed(() => {
      const val = [
        { text: 'Video filename', value: 'video_filename' },
        { text: 'Subject Type', value: 'subject_type' },
        { text: 'Status', value: 'status' },
        { text: 'Size', value: 'size', sortable: false },
        { text: 'Motion', value: 'motion', sortable: false },
        { text: 'Created', value: 'requested_at', sort: (a: string, b: string) => Date.parse(b) - Date.parse(a) },
        { text: 'Results', value: 'results', sortable: false },
      ];

      // If not allowed, remove motion column
      if (!isSupervisor.value) {
        val.splice(val.findIndex((v) => v.value === 'motion'), 1);
      }

      return val;
    });
    const headers_selected = [
      { text: 'Video filename', value: 'video_filename' },
      { text: 'Subject Type', value: 'subject_type' },
    ];
    const currentOptions = ref({ num_samples: 250, threshold_low: 2, threshold_high: 5 });
    const outputData = ref({});
    const gridRef = ref(null);

    function taskStatus(item: Task): string {
      return item.status?.status || 'UNKNOWN';
    }

    function displayStatus(item: Task): string {
      return taskStatus(item).toLowerCase().replace(/_/g, ' ');
    }

    function taskSubjectType(item: Task): string {
      const task = item as Task & { args?: Array<{ subject_type?: string }> };
      return task.args?.[0]?.subject_type || 'N/A';
    }

    function taskName(item: Task): string {
      return item.name || '';
    }

    function motionTaskLabel(item: Task): string {
      const [, suffix = ''] = taskName(item).split('.');
      return suffix.split('_')[1] || '';
    }

    async function fetchTasks() {
      requestVersion += 1;
      stopPolling();
      if (fetchingTasks.value || deletingTasks.value) {
        fetchQueued = true;
        return;
      }
      const version = requestVersion;
      fetchQueued = false;

      const retrieve = async () => {
        fetchingTasks.value = true;
        try {
          const result = await useClient().fetchUserTasks(
            taskTableOptions.value.page,
            taskTableOptions.value.itemsPerPage,
            search.value || '',
          );
          if (version === requestVersion && completedAnalysesOpen.value) {
            tasks.value = result.tasks;
            taskTableInfo.value.total_number = result.total;
          }
        } catch (error) {
          console.warn('Failed to fetch completed analyses', error);
          snackbar.dispatch({
            text: 'Failed to load completed analyses.',
            options: { color: 'error', top: true },
          });
        } finally {
          fetchingTasks.value = false;
        }
      };

      if (!completedAnalysesOpen.value || document.visibilityState !== 'visible') {
        return;
      }

      await retrieve();
      if (fetchQueued) {
        await fetchTasks();
        return;
      }

      // Poll only while this dialog and tab are visible.  Schedule the next
      // request instead of retaining an async loop across dialog close/open.
      if (taskTableOptions.value.page === 1
        && (!taskPollingActive.value || taskTableOptions.value.itemsPerPage !== 15 || !!search.value)
        && ongoingDisplayedTasks.value.length
        && completedAnalysesOpen.value
        && document.visibilityState === 'visible') {
        taskFetchTimeout.value = window.setTimeout(() => {
          taskFetchTimeout.value = null;
          fetchTasks();
        }, 15000);
      }
    }

    function stopPolling() {
      if (taskFetchTimeout.value !== null) {
        window.clearTimeout(taskFetchTimeout.value);
        taskFetchTimeout.value = null;
      }
    }

    function closeDialog() {
      completedAnalysesOpen.value = false;
      stopPolling();
    }

    // If a task is added, re-fetch tasks
    watch(latestAddedTaskId, () => {
      if (completedAnalysesOpen.value) {
        stopPolling();
        fetchTasks();
      }
    });

    watch(taskTableOptions, () => {
      if (completedAnalysesOpen.value) {
        stopPolling();
        fetchTasks();
      }
    }, { deep: true });

    watch(search, () => {
      if (searchTimeout.value !== null) window.clearTimeout(searchTimeout.value);
      searchTimeout.value = window.setTimeout(() => {
        searchTimeout.value = null;
        if (!completedAnalysesOpen.value) return;
        if (taskTableOptions.value.page !== 1) {
          taskTableOptions.value.page = 1;
        } else {
          stopPolling();
          fetchTasks();
        }
      }, 250);
    });

    watch(latestTasks, () => {
      if (completedAnalysesOpen.value
        && taskTableOptions.value.page === 1
        && taskTableOptions.value.itemsPerPage === 15
        && !fetchingTasks.value && !deletingTasks.value
        && !search.value) {
        tasks.value = latestTasks.value;
        taskTableInfo.value.total_number = latestTaskTotal.value;
      }
    });

    // Reset search when dialog opened
    watch(completedAnalysesOpen, (val) => {
      if (!val) {
        requestVersion += 1;
        stopPolling();
        return;
      }
      search.value = null;
      taskTableOptions.value.page = 1;
      fetchTasks();
    });

    // The parent file menu can be mounted after its open state was already set.
    // In that case the watcher above does not observe a transition, so load now.
    onMounted(() => {
      document.addEventListener('visibilitychange', resumePolling);
      if (completedAnalysesOpen.value) {
        search.value = null;
        taskTableOptions.value.page = 1;
        fetchTasks();
      }
    });
    function resumePolling() {
      if (document.visibilityState === 'visible' && completedAnalysesOpen.value) fetchTasks();
      else stopPolling();
    }
    onUnmounted(() => {
      requestVersion += 1;
      stopPolling();
      if (searchTimeout.value !== null) window.clearTimeout(searchTimeout.value);
      document.removeEventListener('visibilitychange', resumePolling);
    });

    function displayProgress(item: Task) {
      if (taskIsFinished(item)) {
        return '';
      }

      const progress = item.status?.progress;
      return progress === undefined ? '' : `${progress}%`;
    }

    function displaySize(item: Task, zip = false) {
      const result = item.status?.result;
      if (!result) {
        return 'N/A';
      }

      const size = zip ? result.zip_size : result.size;
      if (Number.isNaN(Number(size))) {
        return 'N/A';
      }

      return filesize(size);
    }

    const datasetActionsDisabled = (item: Task) => item.status?.result === undefined;
    const isCancellable = (item: Task) => !taskIsFinished(item);
    const isRetryable = (item: Task) => ['FAILURE', 'REVOKED'].includes(taskStatus(item));
    function taskDownloadLink(item: Task, zip = false): string | null {
      if (item.status?.result === undefined) {
        return null;
      }

      const filename = zip ? item.status.result.zip_filename : item.status.result.filename;
      return useClient().getDownloadLink(filename);
    }

    function openDatasetLocation(task: Task): Location | undefined {
      const datasetId = task.status?.result?.dataset_id;
      if (datasetId === undefined) {
        return undefined;
      }

      return {
        name: 'Home',
        query: {
          dataset: datasetId,

          // Always true, since task results are per user
          user: 'true',
        },
      };
    }

    async function deleteTask(task: Task, skipDialog = false) {
      const choice = skipDialog || await prompt.open({
        title: 'Delete this Task?',
        message: 'The task will be deleted from this list, '
        + 'but the associated dataset can still be accessed '
        + 'through the dataset browser, or with its URL.',
        positiveText: 'Yes',
        negativeText: 'No',
      });

      if (choice) {
        const previousTasks = tasks.value;
        tasks.value = tasks.value.filter(({ task_id }) => task_id !== task.task_id);
        try {
          await useClient().deleteTask(task);
        } catch (error) {
          tasks.value = previousTasks;
          throw error;
        }
        if (!skipDialog) await fetchTasks();
      }
    }

    async function cancelTask(task: Task) {
      const choice = await prompt.open({
        title: 'Cancel this Task?',
        message: 'The running analysis will be stopped. You can retry it later.',
        positiveText: 'Cancel Task',
        negativeText: 'Keep Running',
      });
      if (!choice) return;
      const previousTasks = tasks.value;
      tasks.value = tasks.value.map((currentTask) => (currentTask.task_id === task.task_id ? {
        ...currentTask,
        status: { ...currentTask.status, status: 'REVOKED', progress: 0 },
      } : currentTask));
      try {
        await useClient().cancelTask(task);
      } catch (error) {
        tasks.value = previousTasks;
        throw error;
      }
      await fetchTasks();
    }

    async function retryTask(task: Task) {
      const previousTasks = tasks.value;
      tasks.value = tasks.value.map((currentTask) => (currentTask.task_id === task.task_id ? {
        ...currentTask,
        status: { task_id: task.task_id, status: 'PENDING', progress: 0 },
      } : currentTask));
      try {
        await useClient().retryTask(task);
      } catch (error) {
        tasks.value = previousTasks;
        throw error;
      }
      await fetchTasks();
    }

    function formatDateTime(dateString: string): string {
      const timestamp = Date.parse(dateString);
      if (Number.isNaN(timestamp)) {
        return '';
      }
      const date = new Date(dateString);
      return date.toLocaleString();
    }

    function downloadSelectedTasks(zip = false) {
      selectedTasks.value.forEach((task) => {
        const link = taskDownloadLink(task, zip);
        if (link === null) {
          return;
        }

        window.open(link, '_blank');
      });
    }

    async function deleteSelectedTasks() {
      const proceed = await prompt.open({
        title: 'Delete all selected tasks?',
        message: 'The tasks will be deleted from this list, '
        + 'but the associated dataset can still be accessed '
        + 'through the dataset browser, or with its URL.',
        positiveText: 'Yes',
        negativeText: 'No',
      });
      if (!proceed) {
        return;
      }

      deletingTasks.value = true;
      stopPolling();
      requestVersion += 1;
      const failed: Task[] = [];
      try {
        // Sequential deletes prevent one rollback from restoring other deleted rows.
        for (const task of selectedTasks.value.slice()) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await deleteTask(task, true);
          } catch (_) {
            failed.push(task);
          }
        }
        selectedTasks.value = failed;
        if (failed.length) snackbar.dispatch({
          text: `Failed to delete ${failed.length} task(s). Please try again.`,
          options: { color: 'error', top: true },
        });
      } finally {
        deletingTasks.value = false;
        await fetchTasks();
      }
    }

    async function computePost(tasklist: VideoEvaluationResult[], options: StatisticsOptions) {
      // console.log(tasklist);
      try {
        outputData.value = {};
        loadingAnalysis.value = true;
        const postStatistics = await useClient().analyseCsv(tasklist, options);
        outputData.value = postStatistics;
      } catch (e) {
        console.log('Failed to perform the post analysis');
      } finally {
        loadingAnalysis.value = false;
      }
    }
    function exportCSV() {
      if (!gridRef.value) return;
      if (gridRef.value !== null) {
        // eslint-disable-next-line
        (gridRef as any).value.exportTable('csv', false, 'output.csv');
      }
    }
    watch(selectedTasks, () => {
      outputData.value = {};
    });
    return {
      headers,
      headers_selected,
      isSupervisor,
      completedAnalysesOpen,
      tasks,
      selectedTasks,
      downloadSelectedTasks,
      deleteSelectedTasks,
      search,
      taskTableInfo,
      taskTableOptions,
      loading,
      loadingAnalysis,
      closeDialog,
      taskDownloadLink,
      datasetActionsDisabled,
      isCancellable,
      isRetryable,
      taskIsFinished,
      openDatasetLocation,
      deleteTask,
      cancelTask,
      retryTask,
      displayProgress,
      displaySize,
      formatDateTime,
      taskStatus,
      displayStatus,
      taskSubjectType,
      taskName,
      motionTaskLabel,
      postAnalysisDialog,
      computePost,
      currentOptions,
      outputData,
      gridRef,
      exportCSV,
    };
  },
});
